import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Button,
} from "@mui/material";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import BugReportIcon from "@mui/icons-material/BugReport";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { useThemeMode } from "@context/ThemeContext";
import { DRAWER_WIDTH, NAV_DESKTOP_UP, NAV_ITEMS } from "./constants";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiresAuth || isAuthenticated,
  );

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    onMobileClose();
  };

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "en" ? "hr" : "en");
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
        }}
        onClick={() => handleNav("/")}
      >
        <RestaurantMenuIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            {t("app.name")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontSize: 10,
            }}
          >
            {t("app.subtitle")}
          </Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1.5 }}>
        {visibleItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => handleNav(item.path)}
            sx={{ mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon
                sx={{
                  color:
                    location.pathname === item.path
                      ? "primary.main"
                      : "text.secondary",
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={t(item.i18nKey)}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
            />
          </ListItemButton>
        ))}
        <ListItemButton
          component="a"
          href="https://bugs.pinehood.tech/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <BugReportIcon sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText
            primary={t("nav.reportBug")}
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
          />
        </ListItemButton>
      </List>

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={toggleTheme}
          size="small"
          title={mode === "dark" ? "Light mode" : "Dark mode"}
        >
          {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        <Button
          size="small"
          onClick={toggleLang}
          sx={{ minWidth: 40, fontWeight: 600 }}
        >
          {i18n.language === "en" ? "HR" : "EN"}
        </Button>
      </Box>

      {isAuthenticated && (
        <>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <ListItemButton onClick={handleLogout} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={t("nav.signOut")}
                slotProps={{
                  primary: { sx: { fontSize: 14, fontWeight: 500 } },
                }}
              />
            </ListItemButton>
          </Box>
        </>
      )}
    </Box>
  );

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    maxWidth: "min(100vw, 100%)",
    boxSizing: "border-box" as const,
    borderRight: "1px solid",
    borderColor: "divider",
    pt: "env(safe-area-inset-top, 0px)",
    pb: "env(safe-area-inset-bottom, 0px)",
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", [NAV_DESKTOP_UP]: "none" },
          zIndex: (theme) => theme.zIndex.drawer + 2,
          "& .MuiDrawer-paper": drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", [NAV_DESKTOP_UP]: "block" },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            ...drawerPaperSx,
            position: "relative",
            height: "100vh",
            overflowY: "auto",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
